import queryString from 'query-string';

import { getFetchMethod, HttpMethod } from 'src/api/common';
import {
  transformGetAbSectionsResponse,
  transformGetAvailableCountriesResponse,
  transformGetAvailableCurrenciesResponse,
  transformGetOpenedGamesResponse,
  transformGetOpenedSectionsResponse,
  transformGetTopPlayersResponse,
} from 'src/api/transformers/analytics';
import { transformGetGameCollectionSegmentResponse } from 'src/api/transformers/game-collection';
import {
  transformGetGamePromotionCountriesResponse,
  transformGetGamePromotionSummaryResponse,
} from 'src/api/transformers/game-promotion';
import {
  transformGetBrandsOverviewsResponse,
  transformGetPlatformIntegrationStatusesResponse,
  transformGetPlatformsResponse,
  transformGetPlatformTrackingEventsResponse,
} from 'src/api/transformers/platforms';
import { transformGetPriorityImpactTurnoverImpactResponse } from 'src/api/transformers/priority-impact';
import { transformGetGamesRecommendationsResponse } from 'src/api/transformers/recommendations';
import { transformGetUserResponse } from 'src/api/transformers/user';
import { getEnvConfig } from 'src/helpers/config';
import type {
  AbFilters,
  AbMetricsFilters,
  AbSectionsTransformed,
  AvailableCountriesTransformed,
  AvailableCurrenciesTransformed,
  GetABSplitResponse,
  GetAbConversionResponse,
  GetAbMetricsPerPlayerResponse,
  GetAbSectionDailyResponse,
  GetAbSectionGlobalResponse,
  GetAbSectionsResponse,
  GetAbSectionUpliftResponse,
  GetAbTotalStatsResponse,
  GetAbUpliftResponse,
  GetAvailableCountriesResponse,
  GetAvailableCurrenciesResponse,
  GetGlobalMetricsResponse,
  GetHistoricalDataMetaInfoResponse,
  GetOpenedGamesResponse,
  GetOpenedSectionsResponse,
  GetTopPlayersResponse,
  GlobalMetricsRequest,
  OpenedGamesRequest,
  OpenedGamesTransformed,
  OpenedSectionsRequest,
  OpenedSectionsTransformed,
  TopPlayersTransformed,
} from 'src/types/analytics';
import type {
  ChurnRateExportRequest,
  ChurnRateRequest,
  GetChurnRateOverviewResponse,
  GetChurnRateResponse,
  GetGamePreferencesResponse,
} from 'src/types/churn-rate';
import type {
  CreateCostOptimizationCountriesRequest,
  GetCostOptimizationCountriesResponse,
  GetCostOptimizationGamesRequest,
  GetCostOptimizationGamesResponse,
  GetCostOptimizationOverviewResponse,
  GetCostOptimizationProvidersResponse,
  GetCostOptimizationSearchRequest,
  GetCostOptimizationSearchResponse,
  UpsertCostOptimizationGameRequest,
  UpsertCostOptimizationGameResponse,
  UpsertCostOptimizationProviderRequest,
  UpsertCostOptimizationProviderResponse,
} from 'src/types/cost-optimization';
import type {
  GameCollectionSegmentTransformed,
  GetGameCollectionAvailableGamesResponse,
  GetGameCollectionFiltersResponse,
  GetGameCollectionRequest,
  GetGameCollectionResponse,
  GetGameCollectionSegmentResponse,
} from 'src/types/game-collection';
import type {
  GamePromotionCountriesResponse,
  GamePromotionCountriesTransformed,
  GamePromotionCreateRequest,
  GamePromotionDeleteRequest,
  GamePromotionGetRequest,
  GamePromotionResponse,
  GamePromotionSummaryResponse,
  GamePromotionSummaryTransformed,
  GamePromotionUpdateRequest,
} from 'src/types/game-promotion';
import type {
  BrandsOverviewsTransformed,
  GetBrandResponse,
  GetBrandsOverviewsResponseSchema,
  GetPlatformCredentialsResponse,
  GetPlatformDataUploadsResponse,
  GetPlatformIntegrationStatusesResponse,
  GetPlatformResponse,
  GetPlatformsResponse,
  GetPlatformTrackingEventsResponse,
  IntegrationStatusValues,
  PlatformIntegrationStatusesTransformed,
  PlatformsTransformed,
  PlatformTrackingEventsTransformed,
} from 'src/types/platforms';
import type {
  GetPriorityImpactGameDistributionResponse,
  GetPriorityImpactPeriodsResponse,
  GetPriorityImpactTurnoverImpactResponse,
} from 'src/types/priority-impact';
import type { GamesRecommendationsTransformed, GetGamesRecommendationsResponse } from 'src/types/recommendations';
import type { GetUserResponse, SignInRequest, UserTransformed } from 'src/types/user';

const getApiUrl = () => getEnvConfig().apiUrl;

export const signIn = (data: SignInRequest) =>
  getFetchMethod({
    url: `${getApiUrl()}/users/login`,
    method: HttpMethod.POST,
    data,
  });

export const signOut = () =>
  getFetchMethod({
    url: `${getApiUrl()}/users/me/logout`,
    method: HttpMethod.POST,
    data: {
      logout: true,
    },
  });

export const getUser = () =>
  getFetchMethod<GetUserResponse, UserTransformed>(
    {
      url: `${getApiUrl()}/users/me`,
    },
    transformGetUserResponse,
  );

export const getBrand = (brandId: string) =>
  getFetchMethod<GetBrandResponse>({
    url: `${getApiUrl()}/casino/brands/${brandId}`,
  });

export const getPlatforms = () =>
  getFetchMethod<GetPlatformsResponse, PlatformsTransformed>(
    {
      url: `${getApiUrl()}/casino/platforms`,
    },
    transformGetPlatformsResponse,
  );

export const getPlatform = (platformId: string) =>
  getFetchMethod<GetPlatformResponse>({
    url: `${getApiUrl()}/casino/platforms/${platformId}`,
  });

export const getBrandsOverviews = () =>
  getFetchMethod<GetBrandsOverviewsResponseSchema, BrandsOverviewsTransformed>(
    {
      url: `${getApiUrl()}/casino/brandsOverviews`,
    },
    transformGetBrandsOverviewsResponse,
  );

export const getPlatformCredentials = (platformId: string, env: string) =>
  getFetchMethod<GetPlatformCredentialsResponse>({
    url: `${getApiUrl()}/casino/platforms/${platformId}/credentials?env=${env}`,
  });

export const getPlatformDataUploads = (platformId: string, env: string) =>
  getFetchMethod<GetPlatformDataUploadsResponse>({
    url: `${getApiUrl()}/casino/platforms/${platformId}/dataUploadCheckResults?env=${env}&limit=1000`,
  });

export const getPlatformCheckStatus = (platformId: string, env: string) =>
  getFetchMethod<{ status: string }>({
    url: `${getApiUrl()}/casino/platforms/${platformId}/dataUploadCheckResults/submit?env=${env}`,
  });

export const checkPlatformDataUploads = (platformId: string, env: string) =>
  getFetchMethod({
    url: `${getApiUrl()}/casino/platforms/${platformId}/dataUploadCheckResults/submit?env=${env}`,
    method: HttpMethod.POST,
  });

export const getPlatformTrackingEvents = (platformId: string, env: string) =>
  getFetchMethod<GetPlatformTrackingEventsResponse, PlatformTrackingEventsTransformed>(
    {
      url: `${getApiUrl()}/casino/platforms/${platformId}/engagementEvents?env=${env}`,
    },
    transformGetPlatformTrackingEventsResponse,
  );

export const getPlatformIntegrationStatuses = (platformId: string) =>
  getFetchMethod<GetPlatformIntegrationStatusesResponse, PlatformIntegrationStatusesTransformed>(
    {
      url: `${getApiUrl()}/casino/platforms/${platformId}/actionIntegrationStatuses`,
    },
    transformGetPlatformIntegrationStatusesResponse,
  );

export const updatePlatformIntegrationStatuses = (
  platformId: string,
  actionId: string,
  data: { stagingStatus: IntegrationStatusValues; prodStatus: IntegrationStatusValues },
) =>
  getFetchMethod<unknown>({
    url: `${getApiUrl()}/casino/platforms/${platformId}/actionIntegrationStatuses/${actionId}`,
    method: HttpMethod.POST,
    data,
  });

export const getGamesRecommendations = (
  zingBrandId: string,
  data: { model?: string; country?: string; lang?: string; gameType?: string },
) =>
  getFetchMethod<GetGamesRecommendationsResponse, GamesRecommendationsTransformed>(
    {
      url: `${getApiUrl()}/casino/_recs_proxy/api/v1/providers/${zingBrandId}/casinoGamesRecommendations?${queryString.stringify(data)}`,
    },
    transformGetGamesRecommendationsResponse,
  );

export const getPlayerGamesRecommendations = (
  zingBrandId: string,
  playerId: string,
  data: { model?: string; country?: string; gameId?: string; gameProviderId?: string; gameType?: string },
) =>
  getFetchMethod<GetGamesRecommendationsResponse, GamesRecommendationsTransformed>(
    {
      url: `${getApiUrl()}/casino/_recs_proxy/api/v1/providers/${zingBrandId}/players/${playerId}/casinoGamesRecommendations?${queryString.stringify(data)}`,
    },
    transformGetGamesRecommendationsResponse,
  );

export const getABSplit = (brandId: string) =>
  getFetchMethod<GetABSplitResponse>({
    url: `${getApiUrl()}/casino/brands/${brandId}/abSplit`,
  });

export const getHistoricalDataMetaInfo = (brandId: string) =>
  getFetchMethod<GetHistoricalDataMetaInfoResponse>({
    url: `${getApiUrl()}/casino/brands/${brandId}/historicalDataMetaInfo`,
  });

export const getAvailableCountries = (brandId: string) =>
  getFetchMethod<GetAvailableCountriesResponse, AvailableCountriesTransformed>(
    {
      url: `${getApiUrl()}/casino/brands/${brandId}/availableCountries`,
    },
    transformGetAvailableCountriesResponse,
  );

export const getAvailableCurrencies = (brandId: string) =>
  getFetchMethod<GetAvailableCurrenciesResponse, AvailableCurrenciesTransformed>(
    {
      url: `${getApiUrl()}/casino/brands/${brandId}/availableCurrencies`,
    },
    transformGetAvailableCurrenciesResponse,
  );

export const getGlobalMetrics = (brandId: string, data: GlobalMetricsRequest) =>
  getFetchMethod<GetGlobalMetricsResponse>({
    url: `${getApiUrl()}/casino/brands/${brandId}/globalMetrics`,
    method: HttpMethod.POST,
    data,
  });

export const getOpenedSections = (brandId: string, data: OpenedSectionsRequest) =>
  getFetchMethod<GetOpenedSectionsResponse, OpenedSectionsTransformed>(
    {
      url: `${getApiUrl()}/casino/brands/${brandId}/openedSectionsAnalytics`,
      method: HttpMethod.POST,
      data,
    },
    transformGetOpenedSectionsResponse,
  );

export const getOpenedGames = (brandId: string, data: OpenedGamesRequest) =>
  getFetchMethod<GetOpenedGamesResponse, OpenedGamesTransformed>(
    {
      url: `${getApiUrl()}/casino/brands/${brandId}/openedGamesAnalytics`,
      method: HttpMethod.POST,
      data,
    },
    transformGetOpenedGamesResponse,
  );

export const getAbTotalStats = (brandId: string, data: AbFilters & AbMetricsFilters) =>
  getFetchMethod<GetAbTotalStatsResponse>({
    url: `${getApiUrl()}/casino/brands/${brandId}/abTotalStats`,
    method: HttpMethod.POST,
    data,
  });

export const getAbUplift = (brandId: string, data: AbFilters & AbMetricsFilters) =>
  getFetchMethod<GetAbUpliftResponse>({
    url: `${getApiUrl()}/casino/brands/${brandId}/abUpliftData`,
    method: HttpMethod.POST,
    data,
  });

export const getAbMetricsPerPlayer = (brandId: string, data: AbFilters & AbMetricsFilters) =>
  getFetchMethod<GetAbMetricsPerPlayerResponse>({
    url: `${getApiUrl()}/casino/brands/${brandId}/abMetricsPerPlayer`,
    method: HttpMethod.POST,
    data,
  });

export const getAbConversion = (brandId: string, data: AbFilters) =>
  getFetchMethod<GetAbConversionResponse>({
    url: `${getApiUrl()}/casino/brands/${brandId}/abActiveDaysAnalytics`,
    method: HttpMethod.POST,
    data,
  });

export const getAbSections = (brandId: string, data: AbFilters) =>
  getFetchMethod<GetAbSectionsResponse, AbSectionsTransformed>(
    {
      url: `${getApiUrl()}/casino/brands/${brandId}/abSectionAnalytics`,
      method: HttpMethod.POST,
      data,
    },
    transformGetAbSectionsResponse,
  );

export const getAbSectionGlobal = (brandId: string, data: AbFilters & AbMetricsFilters) =>
  getFetchMethod<GetAbSectionGlobalResponse>({
    url: `${getApiUrl()}/casino/brands/${brandId}/abSection/globalStats`,
    method: HttpMethod.POST,
    data,
  });

export const getAbSectionDaily = (brandId: string, data: AbFilters & AbMetricsFilters) =>
  getFetchMethod<GetAbSectionDailyResponse>({
    url: `${getApiUrl()}/casino/brands/${brandId}/abSection/dailyStats`,
    method: HttpMethod.POST,
    data,
  });

export const getAbSectionUplift = (brandId: string, data: AbFilters & AbMetricsFilters) =>
  getFetchMethod<GetAbSectionUpliftResponse>({
    url: `${getApiUrl()}/casino/brands/${brandId}/abSection/uplift`,
    method: HttpMethod.POST,
    data,
  });

export const getTopPlayes = (brandId: string) =>
  getFetchMethod<GetTopPlayersResponse, TopPlayersTransformed>(
    {
      url: `${getApiUrl()}/casino/brands/${brandId}/topPlayers`,
    },
    transformGetTopPlayersResponse,
  );

export const getGamePromotionSummary = (
  data: Pick<GamePromotionGetRequest['payload'], 'active_from_incl' | 'active_to_incl'>,
) =>
  getFetchMethod<GamePromotionSummaryResponse, GamePromotionSummaryTransformed>(
    {
      url: `${getApiUrl()}/casino/gameAdjustments/summariesByBrand?${queryString.stringify(data)}`,
    },
    transformGetGamePromotionSummaryResponse,
  );

export const getGamePromotionCountries = (data: {
  zingBrandId: GamePromotionGetRequest['zingBrandId'];
  payload: Pick<GamePromotionGetRequest['payload'], 'active_from_incl' | 'active_to_incl'>;
}) =>
  getFetchMethod<GamePromotionCountriesResponse, GamePromotionCountriesTransformed>(
    {
      url: `${getApiUrl()}/casino/brands/${data.zingBrandId}/gameAdjustments/summariesByCountry?${queryString.stringify(data.payload)}`,
    },
    transformGetGamePromotionCountriesResponse,
  );

export const getGamePromotion = (data: GamePromotionGetRequest) =>
  getFetchMethod<GamePromotionResponse>({
    url: `${getApiUrl()}/casino/brands/${data.zingBrandId}/gameAdjustments?${queryString.stringify(data.payload)}`,
  });

export const createGamePromotion = (data: GamePromotionCreateRequest) =>
  getFetchMethod({
    url: `${getApiUrl()}/casino/brands/${data.zingBrandId}/gameAdjustments`,
    method: HttpMethod.POST,
    data: data.payload,
  });

export const deleteGamePromotion = (data: GamePromotionDeleteRequest) =>
  getFetchMethod({
    url: `${getApiUrl()}/casino/brands/${data.zingBrandId}/gameAdjustments/stopOperation`,
    method: HttpMethod.POST,
    data: data.payload,
  });

export const updateGamePromotion = (data: GamePromotionUpdateRequest) =>
  getFetchMethod({
    url: `${getApiUrl()}/casino/brands/${data.zingBrandId}/gameAdjustments`,
    method: HttpMethod.PATCH,
    data: data.payload,
  });

export const getChurnRateOverview = () =>
  getFetchMethod<GetChurnRateOverviewResponse>({
    url: `${getApiUrl()}/casino/churnBrandsOverviews`,
  });

export const getChurnRate = (zingBrandId: string, data: ChurnRateRequest) =>
  getFetchMethod<GetChurnRateResponse>({
    url: `${getApiUrl()}/casino/brands/${zingBrandId}/churnPredictions`,
    method: HttpMethod.POST,
    data,
  });

export const exportChurnRate = (data: { payload: ChurnRateExportRequest; brandId: string }) =>
  getFetchMethod<Blob>({
    url: `${getApiUrl()}/casino/brands/${data.brandId}/churnPredictions/export`,
    method: HttpMethod.POST,
    data: data.payload,
    isBlob: true,
  });

export const getGamePrefecences = (zingBrandId: string, playerId: string) =>
  getFetchMethod<GetGamePreferencesResponse>({
    url: `${getApiUrl()}/casino/brands/${zingBrandId}/players/${playerId}/gamePreferences`,
  });

export const getGameCollectionFilters = (zingBrandId: string) =>
  getFetchMethod<GetGameCollectionFiltersResponse>({
    url: `${getApiUrl()}/casino/brands/${zingBrandId}/gamesCollections/filters`,
  });

export const getGameCollectionSegment = (zingBrandId: string) =>
  getFetchMethod<GetGameCollectionSegmentResponse, GameCollectionSegmentTransformed>(
    {
      url: `${getApiUrl()}/casino/brands/${zingBrandId}/gamesCollections/segments/segmentType/country`,
    },
    transformGetGameCollectionSegmentResponse,
  );

export const getGameCollectionAvailableGames = (zingBrandId: string) =>
  getFetchMethod<GetGameCollectionAvailableGamesResponse>({
    url: `${getApiUrl()}/casino/brands/${zingBrandId}/gamesCollections/availableGames`,
  });

export const getGameCollection = (data: GetGameCollectionRequest) =>
  getFetchMethod<GetGameCollectionResponse>({
    url: `${getApiUrl()}/casino/brands/${data.zingBrandId}/gamesCollections/segments/segmentType/country/${data.segmentId}/casinoGamesRecommendations`,
    method: HttpMethod.POST,
    data: data.payload,
  });

export const getGameCollectionCurl = (data: GetGameCollectionRequest) =>
  getFetchMethod<{ curl: string }>({
    url: `${getApiUrl()}/casino/brands/${data.zingBrandId}/gamesCollections/segments/segmentType/country/${data.segmentId}/casinoGamesRecommendations/curl`,
    method: HttpMethod.POST,
    data: data.payload,
  });

export const exportGameCollection = (data: GetGameCollectionRequest) =>
  getFetchMethod<Blob>({
    url: `${getApiUrl()}/casino/brands/${data.zingBrandId}/gamesCollections/segments/segmentType/country/${data.segmentId}/casinoGamesRecommendations/export`,
    method: HttpMethod.POST,
    data: data.payload,
    isBlob: true,
  });

export const getCostOptimizationOverview = () =>
  getFetchMethod<GetCostOptimizationOverviewResponse>({
    url: `${getApiUrl()}/casino/co/brands/summaries`,
  });

export const getCostOptimizationCounties = (zingBrandId: string) =>
  getFetchMethod<GetCostOptimizationCountriesResponse>({
    url: `${getApiUrl()}/casino/co/brands/${zingBrandId}/countries/summaries`,
  });

export const addCostOptimizationCountries = (data: CreateCostOptimizationCountriesRequest) =>
  getFetchMethod<unknown>({
    url: `${getApiUrl()}/casino/co/brands/${data.zingBrandId}/countrySettings`,
    method: HttpMethod.POST,
    data: data.payload,
  });

export const deleteCostOptimizationCountry = ({
  zingBrandId,
  countryIsoCode,
}: {
  zingBrandId: string;
  countryIsoCode: string;
}) =>
  getFetchMethod<unknown>({
    url: `${getApiUrl()}/casino/co/brands/${zingBrandId}/countrySettings/${countryIsoCode}`,
    method: HttpMethod.DELETE,
  });

export const getCostOptimizationProviders = (zingBrandId: string, countryIsoCode: string) =>
  getFetchMethod<GetCostOptimizationProvidersResponse>({
    url: `${getApiUrl()}/casino/co/brands/${zingBrandId}/countries/${countryIsoCode}/providerPromotionSettings`,
  });

export const getCostOptimizationGames = (data: GetCostOptimizationGamesRequest) =>
  getFetchMethod<GetCostOptimizationGamesResponse>({
    url: `${getApiUrl()}/casino/co/brands/${data.zingBrandId}/countries/${data.countryIsoCode}/providers/${data.gameProviderId}/gamePromotionSettings?page=${data.pagination.page}&limit=${data.pagination.limit}`,
  });

export const createCostOptimizationProvider = ({
  zingBrandId,
  countryIsoCode,
  gameProviderId,
  payload,
}: UpsertCostOptimizationProviderRequest) =>
  getFetchMethod<UpsertCostOptimizationProviderResponse>({
    url: `${getApiUrl()}/casino/co/brands/${zingBrandId}/countries/${countryIsoCode}/providers/${gameProviderId}/promotionConfig`,
    method: HttpMethod.POST,
    data: payload,
  });

export const updateCostOptimizationProvider = ({
  zingBrandId,
  countryIsoCode,
  gameProviderId,
  payload,
}: UpsertCostOptimizationProviderRequest) =>
  getFetchMethod<UpsertCostOptimizationProviderResponse>({
    url: `${getApiUrl()}/casino/co/brands/${zingBrandId}/countries/${countryIsoCode}/providers/${gameProviderId}/promotionConfig`,
    method: HttpMethod.PATCH,
    data: payload,
  });

export const createCostOptimizationGame = ({
  zingBrandId,
  countryIsoCode,
  gameProviderId,
  gameId,
  payload,
}: UpsertCostOptimizationGameRequest) =>
  getFetchMethod<UpsertCostOptimizationGameResponse>({
    url: `${getApiUrl()}/casino/co/brands/${zingBrandId}/countries/${countryIsoCode}/providers/${gameProviderId}/games/${gameId}/promotionConfig`,
    method: HttpMethod.POST,
    data: payload,
  });

export const updateCostOptimizationGame = ({
  zingBrandId,
  countryIsoCode,
  gameProviderId,
  gameId,
  payload,
}: UpsertCostOptimizationGameRequest) =>
  getFetchMethod<UpsertCostOptimizationGameResponse>({
    url: `${getApiUrl()}/casino/co/brands/${zingBrandId}/countries/${countryIsoCode}/providers/${gameProviderId}/games/${gameId}/promotionConfig`,
    method: HttpMethod.PATCH,
    data: payload,
  });

export const resetCostOptimizationAllToNeutral = ({
  zingBrandId,
  countryIsoCode,
}: {
  zingBrandId: string;
  countryIsoCode: string;
}) =>
  getFetchMethod<unknown>({
    url: `${getApiUrl()}/casino/co/brands/${zingBrandId}/countries/${countryIsoCode}/promotionConfigs/reset`,
    method: HttpMethod.POST,
    data: {},
  });

export const resetCostOptimizationGamesToNeutral = ({
  zingBrandId,
  countryIsoCode,
}: {
  zingBrandId: string;
  countryIsoCode: string;
}) =>
  getFetchMethod<unknown>({
    url: `${getApiUrl()}/casino/co/brands/${zingBrandId}/countries/${countryIsoCode}/gamePromotionConfigs/reset`,
    method: HttpMethod.POST,
    data: {},
  });

export const getCostOptimizationSearch = (data: GetCostOptimizationSearchRequest) =>
  getFetchMethod<GetCostOptimizationSearchResponse>({
    url: `${getApiUrl()}/casino/co/brands/${data.zingBrandId}/countries/${data.countryIsoCode}/search`,
    method: HttpMethod.POST,
    data: data.payload,
  });

export const getPriorityImpactPeriods = (zingBrandId: string) =>
  getFetchMethod<GetPriorityImpactPeriodsResponse>({
    url: `${getApiUrl()}/casino/brands/${zingBrandId}/priorityImpact/periods`,
  });

export const getPriorityImpactGameDistribution = (
  zingBrandId: string,
  data: { month: string; gameType: string; isZingSection: boolean; currency: string; moneyType: string },
) =>
  getFetchMethod<GetPriorityImpactGameDistributionResponse>({
    url: `${getApiUrl()}/casino/brands/${zingBrandId}/priorityImpact/gameDistributionByPriority?${queryString.stringify(data)}`,
  });

export const getPriorityImpactTurnoverImpact = (
  zingBrandId: string,
  data: { month: string; gameType: string; isZingSection: boolean; currency: string; moneyType: string },
) =>
  getFetchMethod<GetPriorityImpactTurnoverImpactResponse, GetPriorityImpactTurnoverImpactResponse>(
    {
      url: `${getApiUrl()}/casino/brands/${zingBrandId}/priorityImpact/turnoverImpact?${queryString.stringify(data)}`,
    },
    transformGetPriorityImpactTurnoverImpactResponse,
  );
